import type { BulletListDefinition } from '@atlaskit/adf-schema';

import type { SmartLinkListItemType } from '../../layout/types';
import type { TagConverter } from '../types';
import { isHttpUrl } from '../url-safety';

// Narrows a smart-link-list child to a SmartLinkListItemType, so the converter
// can discriminate `<item>` nodes without an unchecked `as` cast. `<item>` is
// not part of the general ParsedElementType union, hence the local guard.
const isSmartLinkListItem = (child: unknown): child is SmartLinkListItemType =>
	typeof child === 'object' && child !== null && (child as { tag?: unknown }).tag === 'item';

export const convertSmartLinkList: TagConverter = (
	luminaNode,
): BulletListDefinition | undefined => {
	const { children } = luminaNode;

	if (typeof children === 'string') {
		return undefined;
	}

	if (!Array.isArray(children) || children.length === 0) {
		return undefined;
	}

	const listItems: BulletListDefinition['content'] = [];

	// Malformed children (non-`item` tags, missing/unsafe URLs) are intentionally
	// skipped rather than throwing: copy/paste must degrade gracefully and never
	// fail mid-conversion on imperfect LLM output. Valid items are still emitted.
	for (const child of children) {
		if (!isSmartLinkListItem(child)) {
			continue;
		}

		const url = child.attributes?.url;

		if (typeof url !== 'string' || url.length === 0) {
			continue;
		}

		if (!isHttpUrl(url)) {
			continue;
		}

		listItems.push({
			type: 'listItem',
			content: [
				{
					type: 'paragraph',
					content: [
						{
							type: 'inlineCard',
							attrs: { url },
						},
					],
				},
			],
		});
	}

	if (listItems.length === 0) {
		return undefined;
	}

	return {
		type: 'bulletList',
		content: listItems,
	};
};
